import { CartProps } from '../models';
import bigDecimal from 'js-big-decimal';
import { getSessionInfo } from '../utils/getSessionInfo';
import { getSession } from '../utils/storage';

type PriceOverrides = {
	[productId: string]: number;
};

export const useCartAnalytics = (cartItems: CartProps['products'] = []) => {
	const { refCartId, companyInfo } = getSessionInfo();
	const overrides: PriceOverrides = (refCartId && (getSession(`price-overrides-${refCartId}`) as any)) || {};

	let cartSum = new bigDecimal(0);
	let cartSumWithoutDiscount = new bigDecimal(0);
	let totalSavings = new bigDecimal(0);

	for (const item of cartItems) {
		const originalPrice = new bigDecimal(item.sellingPrice ?? 0);
		const quantity = new bigDecimal(item.quantity ?? 0);

		const discount = item?.isDiscountValid ? new bigDecimal(item.discount ?? 0) : new bigDecimal(0);

		const overrideExists = overrides?.[item.productId] != null;
		const overriddenPrice = overrideExists ? new bigDecimal(overrides[item.productId]) : originalPrice;

		const priceBeforeDiscount = overriddenPrice;
		const discountedPrice = priceBeforeDiscount.subtract(discount);
		const itemTotal = discountedPrice.multiply(quantity);

		cartSum = cartSum.add(itemTotal);

		const itemTotalOriginal = originalPrice.multiply(quantity);
		const overrideTotal = overriddenPrice.multiply(quantity);

		const itemTotalForSubtotal = overriddenPrice.compareTo(originalPrice) > 0 ? overrideTotal : itemTotalOriginal;

		cartSumWithoutDiscount = cartSumWithoutDiscount.add(itemTotalForSubtotal);

		let itemSavings = itemTotalOriginal.subtract(itemTotal);
		if (itemSavings.compareTo(new bigDecimal(0)) < 0) {
			itemSavings = new bigDecimal(0);
		}

		totalSavings = totalSavings.add(itemSavings);
	}

	const vatAmount = cartSum.multiply(new bigDecimal(+(companyInfo?.tax ?? 0) / 100));
	const payableAmount = cartSum.add(vatAmount);

	return {
		cartLength: cartItems.length,
		cartSum: cartSum.round(2).getValue(),
		cartSumWithoutDiscount: cartSumWithoutDiscount.round(2).getValue(),
		totalSavings: totalSavings.round(2).getValue(),
		vatAmount: vatAmount.round(2).getValue(),
		payableAmount: payableAmount.round(2).getValue(),
	};
};
